import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutContent } from "@/mocks/articles";
import { useState } from "react";

const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于红桃影视",
  description: "红桃影视 - 让每个人都能享受高品质观影体验",
  url: "https://hongtaomovies.com/about",
  isPartOf: {
    "@type": "WebSite",
    name: "红桃影视",
    url: "https://hongtaomovies.com/",
  },
  about: {
    "@type": "Organization",
    name: "红桃影视",
    description: "免费高清影视资源平台",
    email: "contact@hongtaomovies.com",
    url: "https://hongtaomovies.com/",
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://hongtaomovies.com/" },
    { "@type": "ListItem", position: 2, name: "关于我们", item: "https://hongtaomovies.com/about" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "红桃影视是否需要注册才能观看？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "不需要！红桃影视是一个完全免费的在线影视平台，所有资源无需注册、无需登录即可免费观看。",
      },
    },
    {
      "@type": "Question",
      name: "红桃影视的视频画质如何？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "我们提供1080P超清和4K高清两种画质选择。所有资源均经过专业转码优化。",
      },
    },
    {
      "@type": "Question",
      name: "观看过程中出现卡顿怎么办？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "红桃影视支持多条播放线路自动切换。如果遇到卡顿，可以手动切换备用线路。",
      },
    },
    {
      "@type": "Question",
      name: "红桃影视有广告吗？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "红桃影视承诺全程无广告观看体验。我们不会在视频播放前、播放中或暂停时插入任何形式的广告。",
      },
    },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }

    setFormStatus("idle");
    setFormError("");

    try {
      const res = await fetch("https://readdy.ai/api/form/d9qt6cme3oq4jqumtqe0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查连接后重试");
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />
      </div>

      <main>
        {/* Hero */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20background%20with%20soft%20golden%20light%2C%20film%20projector%20beam%2C%20elegant%20minimal%20atmosphere%2C%20abstract%20movie%20theater%20essence%2C%20rich%20warm%20tones%20with%20subtle%20gradients&width=1800&height=500&seq=about-hero&orientation=landscape"
            alt="关于红桃影视"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-background-50"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
              关于<strong className="text-accent-400">红桃影视</strong>
            </h1>
            <p className="text-sm md:text-base text-background-100/80 max-w-xl mx-auto leading-relaxed">
              {aboutContent.brandIntro.title}
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
              品牌<strong>故事</strong>
            </h2>
            <div className="space-y-4">
              {aboutContent.brandIntro.paragraphs.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-background-100/80">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutContent.stats.map((stat, i) => (
                <div key={i} className="text-center p-6 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-xl text-accent-700`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              我们的<strong>理念</strong>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutContent.values.map((v, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center group hover:border-primary-100 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-200 transition-colors">
                    <i className={`${v.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-10 md:py-14 bg-background-100/80">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              用户<strong>口碑</strong>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutContent.testimonials.map((t, i) => (
                <div key={i} className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.text}</p>
                  <p className="text-xs text-foreground-500 font-medium">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              联系<strong>我们</strong>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-foreground-900">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-700"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-700"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-accent-700"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微博</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-700"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">服务时间</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.hours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-4">在线反馈</h3>
                <form className="space-y-4" data-readdy-form="true" onSubmit={handleSubmit}>
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的称呼"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="电子邮箱"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请描述您的问题或建议（500字以内）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                      required
                    ></textarea>
                  </div>
                  <div className="antispam-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-lg font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    提交反馈
                  </button>
                  {formStatus === "success" && (
                    <p className="text-sm text-green-600 text-center">感谢您的反馈，我们会尽快处理！</p>
                  )}
                  {formStatus === "error" && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}